import { useAuth } from "@/contexts/auth.context";
import { ROUTE_FEATURE_MAP } from "@/config/features";

/**
 * Hook para verificar se o usuário tem acesso a uma funcionalidade específica.
 *
 * Uso:
 * const { hasFeature, subscription } = usePlanFeatures();
 *
 * if (hasFeature('dashboard-financeiro')) { ... }
 *
 * Para exibir nomes de planos, use sempre `subscription.plan_name` (plano
 * atual) ou os hooks de `use-public-plans` (planos reais do banco) — nunca
 * rótulos fixos no código.
 */
export function usePlanFeatures() {
  const { user } = useAuth();
  
  const subscription = user?.subscription;
  
  // Verificar se tem subscription ativa
  const hasSubscription = !!subscription && 
    ['active', 'trial'].includes(subscription.status?.toLowerCase());
  
  // Lista de slugs de features habilitadas
  const enabledFeatureSlugs = subscription?.enabled_feature_slugs || [];
  
  // Tipo do plano
  const planSlug = subscription?.plan_slug || '';
  const planType = subscription?.plan_type || '';
  const accountType = subscription?.account_type || '';
  
  // Helpers de tipo de conta (os antigos isPlanBasic/isPlanPro/isPlanFamily
  // dependiam de slugs que não existem mais no banco e foram removidos).
  const isPlanIndividual = accountType === 'individual';
  const isPlanFamilyType = accountType === 'family';
  
  // AI enabled
  const aiEnabled = subscription?.ai_enabled || false;
  const aiInteractionsLimit = subscription?.ai_interactions_limit || 0;
  
  /**
   * Verifica se o usuário tem acesso a uma funcionalidade específica.
   * @param featureSlug - Slug da funcionalidade (ex: 'dashboard-financeiro')
   * @param requireSubscription - Se true, retorna false se não tiver subscription (default: true)
   */
  const hasFeature = (
    featureSlug: string, 
    requireSubscription: boolean = true
  ): boolean => {
    if (requireSubscription && !hasSubscription) {
      return false;
    }
    return enabledFeatureSlugs.includes(featureSlug);
  };
  
  /**
   * Verifica se o usuário pode acessar uma rota específica do sistema.
   * Mapeia rotas para slugs de funcionalidades.
   */
  const canAccessRoute = (route: string): boolean => {
    const routeFeatures = ROUTE_FEATURE_MAP[route];
    if (!routeFeatures) {
      // Se não temos mapeamento específico, permite acesso
      return true;
    }
    
    // Verifica se tem pelo menos uma das features necessárias
    return routeFeatures.some(feature => hasFeature(feature));
  };
  
  /**
   * Retorna a lista de rotas acessíveis para o usuário.
   */
  const getAccessibleRoutes = (routes: string[]): string[] => {
    return routes.filter(route => canAccessRoute(route));
  };
  
  return {
    // Estado
    hasSubscription,
    subscription,
    enabledFeatureSlugs,
    planSlug,
    planType,
    accountType,
    aiEnabled,
    aiInteractionsLimit,
    
    // Helpers de tipo de conta
    isPlanIndividual,
    isPlanFamilyType,
    
    // Funções de verificação
    hasFeature,
    canAccessRoute,
    getAccessibleRoutes,
  };
}

/**
 * Componente para renderizar conteúdo condicionalmente baseado no plano.
 * Uso: <FeatureGuard feature="dashboard-financeiro" fallback={<div>Upgrade seu plano</div>}>
 */
export function FeatureGuard({ 
  feature, 
  children, 
  fallback = null,
  requireSubscription = true 
}: Readonly<{
  feature: string; 
  children: React.ReactNode; 
  fallback?: React.ReactNode | null;
  requireSubscription?: boolean;
}>) {
  const { hasFeature } = usePlanFeatures();
  
  if (!hasFeature(feature, requireSubscription)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

/**
 * Componente para bloquear rotas/funcionalidades quando não tem assinatura.
 * Uso: <SubscriptionGuard> conteúdo restrito </SubscriptionGuard>
 */
export function SubscriptionGuard({ 
  children, 
  fallback 
}: Readonly<{
  children: React.ReactNode; 
  fallback?: React.ReactNode | null;
}>) {
  const { hasSubscription } = usePlanFeatures();
  
  if (!hasSubscription) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

